import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { SignalRHubUrls } from '../../types/constants/apiUrls/signalr/hubUrls';
import { useSignalRHub } from './useSignalRHub';
import {
    CursorPositionPayload,
    PresenceSnapshotPayload,
    RetroCardPayload,
    RetroCardsSnapshotPayload,
    RetroConfettiPayload,
    RetroEventTypes,
    RetroRoomStatePayload,
    UserPresencePayload,
} from './types';

const CURSOR_THROTTLE_MS = 25;
const CONFETTI_DURATION_MS = 3500;

export type UseRetroPresenceOptions = {
    roomId: string;
    enabled?: boolean;
    surfaceRef?: React.RefObject<HTMLElement | null>;
    trackCursor?: boolean;
};

export type UseRetroPresenceResult = {
    connectionState: ReturnType<typeof useSignalRHub>['connectionState'];
    isHubReady: boolean;
    lastError: string | null;
    localConnectionId: string;
    members: UserPresencePayload[];
    cursors: Record<string, CursorPositionPayload>;
    localCursor: { x: number; y: number } | null;
    cards: RetroCardPayload[];
    confettiBurstId: number;
    lastConfetti: RetroConfettiPayload | null;
    activeCount: number;
    isRoomAdmin: boolean;
    contentHidden: boolean;
    adminDisplayName: string;
    adminConnectionId: string;
    sendCursorPosition: (clientX: number, clientY: number) => void;
    addCard: (x: number, y: number) => Promise<RetroCardPayload>;
    updateCardText: (cardId: string, text: string) => Promise<void>;
    moveCard: (cardId: string, x: number, y: number) => Promise<void>;
    deleteCard: (cardId: string) => Promise<void>;
    triggerConfetti: () => Promise<void>;
    setContentHidden: (hidden: boolean) => Promise<void>;
};

function memberKey(p: { connectionId?: string; userRef?: string; displayName?: string }): string {
    if (p.displayName) return `name:${p.displayName.trim().toLowerCase()}`;
    if (p.userRef) return `ref:${p.userRef}`;
    return p.connectionId || '';
}

export function useRetroPresence(options: UseRetroPresenceOptions): UseRetroPresenceResult {
    const { roomId, enabled = true, surfaceRef, trackCursor = true } = options;
    const [members, setMembers] = useState<UserPresencePayload[]>([]);
    const [cursors, setCursors] = useState<Record<string, CursorPositionPayload>>({});
    const [localCursor, setLocalCursor] = useState<{ x: number; y: number } | null>(null);
    const [cards, setCards] = useState<RetroCardPayload[]>([]);
    const [confettiBurstId, setConfettiBurstId] = useState(0);
    const [lastConfetti, setLastConfetti] = useState<RetroConfettiPayload | null>(null);
    const [activeCount, setActiveCount] = useState(0);
    const [localConnectionId, setLocalConnectionId] = useState('');
    const [roomState, setRoomState] = useState<RetroRoomStatePayload | null>(null);
    const lastSentRef = useRef(0);
    const connectionRef = useRef<HubConnection | null>(null);
    const confettiTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const applyRoomState = useCallback((payload: unknown) => {
        const state = normalizeRoomState(payload);
        if (!state) return;
        setRoomState(state);
    }, []);

    const fireConfetti = useCallback((payload: RetroConfettiPayload | null) => {
        setLastConfetti(payload);
        setConfettiBurstId((id) => id + 1);
        if (confettiTimerRef.current) {
            clearTimeout(confettiTimerRef.current);
        }
        confettiTimerRef.current = setTimeout(() => {
            setConfettiBurstId(0);
            confettiTimerRef.current = null;
        }, CONFETTI_DURATION_MS);
    }, []);

    const upsertCard = useCallback((payload: unknown) => {
        const card = normalizeCard(payload);
        if (!card) return;
        setCards((prev) => {
            const without = prev.filter((c) => c.id !== card.id);
            return [...without, card];
        });
    }, []);

    const applyCursor = useCallback((payload: unknown) => {
        const c = normalizeCursor(payload);
        if (!c) return;
        const key = memberKey(c);
        if (!key) return;
        setCursors((prev) => ({ ...prev, [key]: c }));
    }, []);

    const join = useCallback(async (connection: HubConnection) => {
        connectionRef.current = connection;
        setLocalConnectionId(connection.connectionId || '');
        setCards([]);
        setRoomState(null);
        await connection.invoke('JoinRoom', roomId);
    }, [roomId]);

    const leave = useCallback(async (connection: HubConnection) => {
        await connection.invoke('LeaveRoom', roomId);
        if (connectionRef.current === connection) {
            connectionRef.current = null;
        }
        setLocalCursor(null);
        setCursors({});
        setCards([]);
        setRoomState(null);
        setLocalConnectionId('');
    }, [roomId]);

    const eventHandlers = useMemo(
        () => ({
            [RetroEventTypes.UserJoined]: (payload: unknown) => {
                const p = normalizePresence(payload);
                if (!p) return;
                const key = memberKey(p);
                setActiveCount(p.activeCount);
                setMembers((prev) => {
                    const without = prev.filter((m) => memberKey(m) !== key);
                    return [...without, p];
                });
            },
            [RetroEventTypes.UserLeft]: (payload: unknown) => {
                const p = normalizePresence(payload);
                if (!p) return;
                const key = memberKey(p);
                setActiveCount(p.activeCount);
                setMembers((prev) => prev.filter((m) => memberKey(m) !== key));
                setCursors((prev) => {
                    const next = { ...prev };
                    delete next[key];
                    return next;
                });
            },
            [RetroEventTypes.PresenceSnapshot]: (payload: unknown) => {
                const snap = normalizeSnapshot(payload);
                if (!snap) return;
                setActiveCount(snap.activeCount);
                setMembers(snap.members || []);
            },
            [RetroEventTypes.CursorMoved]: (payload: unknown) => applyCursor(payload),
            [RetroEventTypes.CardsSnapshot]: (payload: unknown) => {
                const snap = normalizeCardsSnapshot(payload);
                if (!snap) return;
                setCards(snap.cards || []);
            },
            [RetroEventTypes.CardAdded]: (payload: unknown) => upsertCard(payload),
            [RetroEventTypes.CardUpdated]: (payload: unknown) => upsertCard(payload),
            [RetroEventTypes.CardMoved]: (payload: unknown) => upsertCard(payload),
            [RetroEventTypes.CardDeleted]: (payload: unknown) => {
                const card = normalizeCard(payload);
                if (!card?.id) return;
                setCards((prev) => prev.filter((c) => c.id !== card.id));
            },
            [RetroEventTypes.Confetti]: (payload: unknown) => {
                fireConfetti(normalizeConfetti(payload));
            },
            [RetroEventTypes.RoomState]: (payload: unknown) => applyRoomState(payload),
        }),
        [applyCursor, applyRoomState, fireConfetti, upsertCard]
    );

    const { connection, connectionState, lastError } = useSignalRHub({
        hubUrl: SignalRHubUrls.Retro,
        protocol: 'json',
        groupKey: `retro:${roomId}`,
        enabled: enabled && !!roomId,
        join,
        leave,
        eventHandlers,
    });

    const isHubReady =
        connectionState === 'connected' ||
        connection?.state === HubConnectionState.Connected ||
        connectionRef.current?.state === HubConnectionState.Connected;

    useEffect(() => {
        connectionRef.current = connection;
        if (connection?.connectionId) {
            setLocalConnectionId(connection.connectionId);
        }
        if (!connection) return;

        const onCursorMoved = (payload: unknown) => applyCursor(payload);
        const onCardsSnapshot = (payload: unknown) => {
            const snap = normalizeCardsSnapshot(payload);
            if (snap) setCards(snap.cards || []);
        };
        const onCardAdded = (payload: unknown) => upsertCard(payload);
        const onCardUpdated = (payload: unknown) => upsertCard(payload);
        const onCardMoved = (payload: unknown) => upsertCard(payload);
        const onCardDeleted = (payload: unknown) => {
            const card = normalizeCard(payload);
            if (!card?.id) return;
            setCards((prev) => prev.filter((c) => c.id !== card.id));
        };
        const onConfetti = (payload: unknown) => {
            fireConfetti(normalizeConfetti(payload));
        };
        const onRoomState = (payload: unknown) => applyRoomState(payload);

        connection.on('CursorMoved', onCursorMoved);
        connection.on('CardsSnapshot', onCardsSnapshot);
        connection.on('CardAdded', onCardAdded);
        connection.on('CardUpdated', onCardUpdated);
        connection.on('CardMoved', onCardMoved);
        connection.on('CardDeleted', onCardDeleted);
        connection.on('Confetti', onConfetti);
        connection.on('RoomState', onRoomState);

        return () => {
            connection.off('CursorMoved', onCursorMoved);
            connection.off('CardsSnapshot', onCardsSnapshot);
            connection.off('CardAdded', onCardAdded);
            connection.off('CardUpdated', onCardUpdated);
            connection.off('CardMoved', onCardMoved);
            connection.off('CardDeleted', onCardDeleted);
            connection.off('Confetti', onConfetti);
            connection.off('RoomState', onRoomState);
        };
    }, [connection, applyCursor, applyRoomState, fireConfetti, upsertCard]);

    useEffect(() => {
        return () => {
            if (confettiTimerRef.current) {
                clearTimeout(confettiTimerRef.current);
            }
        };
    }, []);

    const sendCursorPosition = useCallback(
        (clientX: number, clientY: number) => {
            if (!trackCursor) return;

            const el = surfaceRef?.current;
            const rect = el?.getBoundingClientRect();
            if (!rect || rect.width <= 0 || rect.height <= 0) return;

            const x = clamp01((clientX - rect.left) / rect.width);
            const y = clamp01((clientY - rect.top) / rect.height);
            setLocalCursor({ x, y });

            const conn = connectionRef.current;
            if (!conn || conn.state !== HubConnectionState.Connected) return;

            const now = Date.now();
            if (now - lastSentRef.current < CURSOR_THROTTLE_MS) return;
            lastSentRef.current = now;

            void conn.invoke('SendCursorPosition', roomId, x, y).catch((err) => {
                console.warn('[SignalR] SendCursorPosition failed', err);
            });
        },
        [roomId, surfaceRef, trackCursor]
    );

    const invokeOrThrow = useCallback(async <T = void>(method: string, ...args: unknown[]): Promise<T> => {
        const conn = connectionRef.current;
        if (!conn || conn.state !== HubConnectionState.Connected) {
            throw new Error('SignalR connection is not ready');
        }
        return conn.invoke<T>(method, ...args);
    }, []);

    const addCard = useCallback(
        async (x: number, y: number) => {
            const saved = await invokeOrThrow<unknown>('AddCard', roomId, x, y);
            const card = normalizeCard(saved);
            if (card) {
                upsertCard(card);
                return card;
            }
            throw new Error('Invalid card payload');
        },
        [invokeOrThrow, roomId, upsertCard]
    );

    const updateCardText = useCallback(
        async (cardId: string, text: string) => {
            setCards((prev) => prev.map((c) => (c.id === cardId ? { ...c, text } : c)));
            await invokeOrThrow('UpdateCardText', roomId, cardId, text);
        },
        [invokeOrThrow, roomId]
    );

    const moveCard = useCallback(
        async (cardId: string, x: number, y: number) => {
            setCards((prev) => prev.map((c) => (c.id === cardId ? { ...c, x, y } : c)));
            await invokeOrThrow('MoveCard', roomId, cardId, x, y);
        },
        [invokeOrThrow, roomId]
    );

    const deleteCard = useCallback(
        async (cardId: string) => {
            setCards((prev) => prev.filter((c) => c.id !== cardId));
            await invokeOrThrow('DeleteCard', roomId, cardId);
        },
        [invokeOrThrow, roomId]
    );

    const triggerConfetti = useCallback(async () => {
        const payload = await invokeOrThrow<unknown>('TriggerConfetti', roomId);
        fireConfetti(normalizeConfetti(payload));
    }, [fireConfetti, invokeOrThrow, roomId]);

    const setContentHidden = useCallback(
        async (hidden: boolean) => {
            setRoomState((prev) => (prev ? { ...prev, contentHidden: hidden } : prev));
            const payload = await invokeOrThrow<unknown>('SetContentHidden', roomId, hidden);
            applyRoomState(payload);
        },
        [applyRoomState, invokeOrThrow, roomId]
    );

    const isRoomAdmin = !!(localConnectionId && roomState?.adminConnectionId === localConnectionId);
    const contentHidden = !!roomState?.contentHidden;

    return {
        connectionState,
        isHubReady,
        lastError,
        localConnectionId,
        members,
        cursors,
        localCursor,
        cards,
        confettiBurstId,
        lastConfetti,
        activeCount,
        isRoomAdmin,
        contentHidden,
        adminDisplayName: roomState?.adminDisplayName || '',
        adminConnectionId: roomState?.adminConnectionId || '',
        sendCursorPosition,
        addCard,
        updateCardText,
        moveCard,
        deleteCard,
        triggerConfetti,
        setContentHidden,
    };
}

function clamp01(value: number): number {
    if (value < 0) return 0;
    if (value > 1) return 1;
    return value;
}

function pickString(p: Record<string, unknown>, camel: string, pascal: string): string {
    return String(p[camel] ?? p[pascal] ?? '');
}

function pickNumber(p: Record<string, unknown>, camel: string, pascal: string): number {
    return Number(p[camel] ?? p[pascal] ?? 0);
}

function pickBool(p: Record<string, unknown>, camel: string, pascal: string): boolean {
    const v = p[camel] ?? p[pascal];
    return v === true || v === 'true';
}

function normalizePresence(payload: unknown): UserPresencePayload | null {
    if (!payload || typeof payload !== 'object') return null;
    const p = payload as Record<string, unknown>;
    return {
        connectionId: pickString(p, 'connectionId', 'ConnectionId'),
        userRef: pickString(p, 'userRef', 'UserRef'),
        displayName: pickString(p, 'displayName', 'DisplayName'),
        roomId: pickString(p, 'roomId', 'RoomId'),
        activeCount: pickNumber(p, 'activeCount', 'ActiveCount'),
    };
}

function normalizeCursor(payload: unknown): CursorPositionPayload | null {
    if (!payload || typeof payload !== 'object') return null;
    const p = payload as Record<string, unknown>;
    return {
        connectionId: pickString(p, 'connectionId', 'ConnectionId'),
        userRef: pickString(p, 'userRef', 'UserRef'),
        displayName: pickString(p, 'displayName', 'DisplayName'),
        roomId: pickString(p, 'roomId', 'RoomId'),
        x: pickNumber(p, 'x', 'X'),
        y: pickNumber(p, 'y', 'Y'),
    };
}

function normalizeSnapshot(payload: unknown): PresenceSnapshotPayload | null {
    if (!payload || typeof payload !== 'object') return null;
    const p = payload as Record<string, unknown>;
    const rawMembers = (p.members ?? p.Members ?? []) as unknown[];
    return {
        roomId: pickString(p, 'roomId', 'RoomId'),
        activeCount: pickNumber(p, 'activeCount', 'ActiveCount'),
        members: rawMembers.map(normalizePresence).filter(Boolean) as UserPresencePayload[],
    };
}

function normalizeCard(payload: unknown): RetroCardPayload | null {
    if (!payload || typeof payload !== 'object') return null;
    const p = payload as Record<string, unknown>;
    const id = pickString(p, 'id', 'Id');
    if (!id) return null;
    return {
        id,
        roomId: pickString(p, 'roomId', 'RoomId'),
        text: pickString(p, 'text', 'Text'),
        x: pickNumber(p, 'x', 'X'),
        y: pickNumber(p, 'y', 'Y'),
        color: pickString(p, 'color', 'Color') || '#fef08a',
        createdByUserRef: pickString(p, 'createdByUserRef', 'CreatedByUserRef'),
        createdByConnectionId: pickString(p, 'createdByConnectionId', 'CreatedByConnectionId'),
        createdByDisplayName: pickString(p, 'createdByDisplayName', 'CreatedByDisplayName'),
        updatedByUserRef: pickString(p, 'updatedByUserRef', 'UpdatedByUserRef'),
        updatedByDisplayName: pickString(p, 'updatedByDisplayName', 'UpdatedByDisplayName'),
        updatedAtUtc: pickString(p, 'updatedAtUtc', 'UpdatedAtUtc'),
    };
}

function normalizeCardsSnapshot(payload: unknown): RetroCardsSnapshotPayload | null {
    if (!payload || typeof payload !== 'object') return null;
    const p = payload as Record<string, unknown>;
    const raw = (p.cards ?? p.Cards ?? []) as unknown[];
    return {
        roomId: pickString(p, 'roomId', 'RoomId'),
        cards: raw.map(normalizeCard).filter(Boolean) as RetroCardPayload[],
    };
}

function normalizeConfetti(payload: unknown): RetroConfettiPayload | null {
    if (!payload || typeof payload !== 'object') return null;
    const p = payload as Record<string, unknown>;
    return {
        roomId: pickString(p, 'roomId', 'RoomId'),
        connectionId: pickString(p, 'connectionId', 'ConnectionId'),
        userRef: pickString(p, 'userRef', 'UserRef'),
        displayName: pickString(p, 'displayName', 'DisplayName'),
    };
}

function normalizeRoomState(payload: unknown): RetroRoomStatePayload | null {
    if (!payload || typeof payload !== 'object') return null;
    const p = payload as Record<string, unknown>;
    return {
        roomId: pickString(p, 'roomId', 'RoomId'),
        adminConnectionId: pickString(p, 'adminConnectionId', 'AdminConnectionId'),
        adminUserRef: pickString(p, 'adminUserRef', 'AdminUserRef'),
        adminDisplayName: pickString(p, 'adminDisplayName', 'AdminDisplayName'),
        contentHidden: pickBool(p, 'contentHidden', 'ContentHidden'),
    };
}
